import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Switch, Tooltip, Button, Alert, Spin, Table } from 'antd';
import { Icon } from '@eduzz/ui-antd-theme';

import { Formik, Form as FormikForm, Field, FieldArray } from 'formik';
import type { FormikHelpers } from 'formik';
import type IProduct from 'interfaces/IProduct';
import { CharacterCounterFormField } from 'v2/components/Shared/FormFields/CharacterCounterFormField';
import CurrencyFormField from 'v2/components/Shared/FormFields/CurrencyFormField';
import TextFormField from 'v2/components/Shared/FormFields/TextFormField';
import { OfferCurrencyAlert } from 'v2/components/Shared/OfferCurrencyAlert';
import { Space } from 'v2/components/Shared/Space';
import { getCurrencySymbol } from 'v2/helpers/getCurrencySymbol';
import { useCurrencies } from 'v2/hooks/useCurrencies';
import { useUpdateProductOffer } from 'v2/hooks/useUpdateProductOffer';
import type { Currency } from 'v2/interfaces/currency';
import type { ProductOffer } from 'v2/interfaces/product';
import * as yup from 'yup';

import { ChooseCurrenciesModal } from '../ChooseCurrenciesModal';

const MIN_CURRENCY_VALUE = 5;

const validationSchema = yup.object().shape({
  name: yup.string().required('Nome da oferta é obrigatório').min(6).max(100),
  description: yup.string().optional().min(100).max(4000),
  prices: yup
    .array()
    .of(
      yup.object().shape({
        value: yup.number().required().min(MIN_CURRENCY_VALUE, 'O valor deve ser no mínimo 5'),
        currency_code: yup.string().required()
      })
    )
    .required()
});

type FormValues = {
  name: string;
  description: string;
  is_visible_affiliate: boolean;
  prices: Array<{
    value: number;
    currency_code: string;
  }>;
};

type FormProps = {
  product: IProduct;
  offer: ProductOffer;
};

function Form({ product, offer }: FormProps) {
  const offerPrices = React.useMemo(
    () =>
      offer.prices.map(price => ({
        value: price.value,
        currency_code: price.currency.code
      })),
    [offer.prices]
  );
  const history = useHistory();
  const [isChooseCurrenciesModalOpen, setIsChooseCurrenciesModalOpen] = React.useState(false);
  const [selectedCurrencies, setSelectedCurrencies] = React.useState<Currency[]>([]);
  const currenciesQuery = useCurrencies();
  const updateOfferMutation = useUpdateProductOffer({
    onSuccess: () => {
      history.push('/producer/product/editproduct/' + product.id + '/sales');
      toast.success('Oferta atualizada com sucesso');
    },
    onError: () => {
      toast.error('Erro ao atualizar oferta');
    }
  });

  const brlPrice = React.useMemo(() => offerPrices.find(price => price.currency_code === 'BRL'), [offerPrices]);
  const sortedInternationalPrices = React.useMemo(() => {
    const internationalPrices = offerPrices.filter(price => price.currency_code !== 'BRL');
    return internationalPrices.sort((a, b) => a.currency_code.localeCompare(b.currency_code));
  }, [offerPrices]);

  React.useEffect(() => {
    if (!currenciesQuery.isLoading && currenciesQuery.data) {
      const internationalCurrencies = currenciesQuery.data.filter(currency => currency.code !== 'BRL');
      const sortedCurrencies = internationalCurrencies.sort((a, b) => a.code.localeCompare(b.code));
      const filteredCurrencies = sortedCurrencies.filter(currency => {
        return sortedInternationalPrices.some(price => price.currency_code === currency.code);
      });
      setSelectedCurrencies(filteredCurrencies);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currenciesQuery.isLoading, currenciesQuery.data]);

  function addCurrencies(currencies: Currency[]) {
    setSelectedCurrencies([...selectedCurrencies, ...currencies]);
  }

  function removeCurrency(currencyId: number) {
    setSelectedCurrencies(selectedCurrencies.filter(selectedCurrency => selectedCurrency.id !== currencyId));
  }

  function handleCancel() {
    history.push('/producer/product/editproduct/' + product.id + '/sales');
  }

  const onSubmit = React.useCallback(
    async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
      if (values.description === '') {
        values.description = null;
      }

      await updateOfferMutation.mutateAsync({
        productId: product.id,
        offerId: offer.id,
        ...values
      });
      setSubmitting(false);
    },
    [updateOfferMutation, product.id, offer.id]
  );

  return (
    <Formik
      initialValues={{
        name: offer.name,
        description: offer.description || '',
        is_visible_affiliate: offer.is_visible_affiliate,
        prices: [brlPrice, ...sortedInternationalPrices]
      }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {({ isSubmitting, values, handleChange }) => (
        <FormikForm className='flex flex-col gap-6'>
          <Field id='offer-name' name='name' component={TextFormField} label='Nome da oferta' maxLength={255} />
          <Field
            id='offer-description'
            name='description'
            component={CharacterCounterFormField}
            label='Descrição da oferta para checkout (opcional)'
            minimum={100}
            limits={4000}
          />
          <div className='flex items-center gap-2'>
            <Switch
              checked={values.is_visible_affiliate}
              onChange={checked => handleChange({ target: { name: 'is_visible_affiliate', value: checked } })}
            />
            <span>Visível para afiliados</span>
          </div>
          <FieldArray name='prices'>
            {({ push, remove }) => (
              <>
                <div className='bg-surface-default rounded-sm p-4 shadow-sm'>
                  <Table
                    size='small'
                    pagination={false}
                    rowKey='key'
                    dataSource={[{ key: 'BRL' }]}
                    columns={[
                      {
                        title: 'Moeda',
                        key: 'currency',
                        width: 200,
                        render: () => 'BRL (Real Brasileiro)'
                      },
                      {
                        title: 'Valor',
                        key: 'value',
                        width: 200,
                        render: () => (
                          <CurrencyFormField
                            id='offer-price-BRL'
                            name='prices.0.value'
                            inputProps={{
                              maxLength: 20
                            }}
                            InputProps={{
                              startAdornment: <span className='text-content-subtitle mr-1'>R$</span>
                            }}
                          />
                        )
                      },
                      {
                        title: '',
                        key: 'actions'
                      }
                    ]}
                  />
                  <Space size={6} axis='vertical' />
                  <Alert
                    type='info'
                    message='É obrigatória a configuração do valor em real para a conversão automática de suas vendas.'
                    showIcon
                  />
                  <Space size={32} axis='vertical' />
                  <div className='flex items-center justify-between gap-2 max-sm:flex-col max-sm:items-end'>
                    <span>Cadastre valores diferenciados para moedas internacionais</span>
                    <Button onClick={() => setIsChooseCurrenciesModalOpen(true)}>Cadastrar novo valor</Button>
                  </div>
                  <Space size={16} axis='vertical' />
                  <Table
                    size='small'
                    pagination={false}
                    rowKey={record => record.id}
                    dataSource={selectedCurrencies}
                    locale={{ emptyText: 'Nenhuma moeda cadastrada' }}
                    columns={[
                      {
                        title: 'Moeda',
                        key: 'currency',
                        width: 200,
                        render: (_, row) => `${row.code} (${row.name})`
                      },
                      {
                        title: 'Valor',
                        key: 'value',
                        render: (_, row, index) => (
                          <CurrencyFormField
                            id={`offer-price-${row.code}`}
                            name={`prices.${index + 1}.value`}
                            currency={row.code}
                            inputProps={{
                              maxLength: 20
                            }}
                            InputProps={{
                              startAdornment: (
                                <span className='text-content-subtitle mr-1'>
                                  {getCurrencySymbol('pt-BR', row.code)}
                                </span>
                              )
                            }}
                          />
                        )
                      },
                      {
                        title: 'Ações',
                        key: 'actions',
                        align: 'right',
                        render: (_, row, index) => (
                          <Tooltip title='Remover' placement='top'>
                            <Button
                              type='text'
                              shape='circle'
                              id={`remove-currency-${row.id}`}
                              icon={<Icon.Trash2  />}
                              onClick={() => {
                                remove(index + 1);
                                removeCurrency(row.id);
                              }}
                            />
                          </Tooltip>
                        )
                      }
                    ]}
                  />
                  <Space size={16} axis='vertical' />
                  <OfferCurrencyAlert />
                </div>
                <footer className='flex justify-end gap-4'>
                  <Button id='cancel-button' onClick={handleCancel}>
                    Cancelar
                  </Button>
                  <Button
                    id='update-offer-button'
                    type='primary'
                    htmlType='submit'
                    disabled={isSubmitting}
                    icon={isSubmitting ? <Spin size='small' /> : undefined}
                  >
                    Atualizar oferta
                  </Button>
                </footer>
                <ChooseCurrenciesModal
                  isOpen={isChooseCurrenciesModalOpen}
                  closeModal={() => setIsChooseCurrenciesModalOpen(false)}
                  selectedCurrencies={selectedCurrencies}
                  addCurrencies={currencies => {
                    currencies.forEach(currency =>
                      push({
                        value: 0,
                        currency_code: currency.code
                      })
                    );
                    addCurrencies(currencies);
                  }}
                />
              </>
            )}
          </FieldArray>
        </FormikForm>
      )}
    </Formik>
  );
}

export { Form };
